import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import Moment from "react-moment";
import moment from "moment";
import {
  Button,
  Card,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  CardHeader,
  CardBody,
  Badge,
} from "reactstrap";

import salesMembershipService from "../../services/api/sales-membership-service.";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faCheck, faSyncAlt, faCheckCircle, faTimesCircle, faReceipt, faInfoCircle, faChartPie } from "@fortawesome/free-solid-svg-icons";
import ReactBSAlert from "react-bootstrap-sweetalert";
import "react-toastify/dist/ReactToastify.css";
import { toast, ToastContainer } from "react-toastify";

const DetailedView = () => {
  const { id } = useParams();
  const [selectedSales, setSelectedSales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedSales, setCopiedSales] = useState(false);
  const [copiedMem, setCopiedMem] = useState(false);
  const [alert, setAlert] = useState(null);

  const confirmReprocessEpaper2 = useCallback((sale) => {
  const handleReprocess = async () => {
    try {
      const response = await salesMembershipService.reprocessEpaperPlan(sale.pg_subscription_id, sale.plan_id, sale.transaction_id, sale.subscription_id._id); // Replace with your actual API function and parameters
        setAlert(null);
        console.log("Reprocess successful:", response);
        if (response?.data?.status && response?.data?.data?.status) {
          toast.success("Epaper Combo Plan reprocessed successfully!");
        }
        else {
          toast.error("An unexpected error occurred");
        }
    } catch (error) {
      // Handle error (optional)
      console.error("Reprocess failed:", error);
      setAlert(null); // Close the alert
      toast.error("Failed to reprocess Epaper Combo Plan. Please try again.");
    }
  };

  setAlert(
    <ReactBSAlert
      warning
      style={{ display: "block", marginTop: "-100px" }}
      title="Do you want to reprocess this Epaper Combo Plan?"
      onConfirm={() => {handleReprocess(sale)}}
      onCancel={() => setAlert(null)}
      confirmBtnBsStyle="success"
      cancelBtnBsStyle="danger"
      confirmBtnText="Yes, reprocess"
      cancelBtnText="No"
      showCancel
    >
      <p>This action will reprocess the selected Epaper Combo Plan.</p>
    </ReactBSAlert>
  );
}, []);


  const handleCopySales = () => {
    navigator.clipboard.writeText(selectedSales?._id || "-");
    setCopiedSales(true);
    setTimeout(() => setCopiedSales(false), 1000);
  };

  const handleCopyMem = () => {
    navigator.clipboard.writeText(selectedSales?.pg_subscription_id || "-");
    setCopiedMem(true);
    setTimeout(() => setCopiedMem(false), 1000);
  };

  const fetchSalesMembershipDetails = async () => {
    setLoading(true);
    try {
      const response = await salesMembershipService.getDetails(id);
      if (response && response.status === true && response.data) {
        setSelectedSales(response.data);
      } else {
        setError("Failed to load membership details");
      }
    } catch (error) {
      console.error("Error fetching sales membership details:", error);
      setError("Error loading data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesMembershipDetails();
  }, [id]);

  const getStatusBadge = (status) => {
    if (!status) return <Badge color="secondary">Unknown</Badge>;

    switch (status.toLowerCase()) {
      case "created":
        return <Badge color="success">Created</Badge>;
      case "completed":
        return <Badge color="success">Completed</Badge>;
      case "activated":
        return <Badge color="success">Activated</Badge>;
      case "cancelled":
        return <Badge color="danger">Cancelled</Badge>;
      case "pending":
        return <Badge color="warning">Pending</Badge>;
      case "failed":
        return <Badge color="danger">Failed</Badge>;
      default:
        return <Badge color="info">{status}</Badge>;
    }
  };

  const formatCurrency = (value, currency = "USD") => {
    if (!value) return "-";
    return `${value} ${currency}`;
  };

  if (loading) {
    return (
      <div
        className="content d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <Spinner color="primary" />
        <span className="ml-2">Loading membership details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content">
        <Container>
          <Alert color="danger">
            {error}
            <div className="mt-3">
              <Button color="primary" onClick={fetchSalesMembershipDetails}>
                Try Again
              </Button>
              <Button
                className="ml-2"
                color="secondary"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </div>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="content d-flex justify-content-center" style={{ minHeight: '100vh' }}>
      {alert}
      <Container fluid>
        <Card className="shadow-sm mb-4">
          <CardHeader>
            <div className="d-flex align-items-center justify-content-between">
              <div className="h4 mb-0">Sales Membership Detailed View</div>
              <div>
                <Button
                  color="info"
                  size="sm"
                  className="mr-2"
                  onClick={fetchSalesMembershipDetails}
                >
                  <i className="fa fa-refresh mr-1"></i> Refresh
                </Button>
                <Button
                  color="warning"
                  size="sm"
                  onClick={() => window.history.back()}
                >
                  <i className="fa fa-arrow-left mr-1"></i> Back
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <Row>
              {/* Summary Card */}
              <Col md="12" className="mb-4">
                <Card className="border bg-light">
                  <CardBody>
                    <Row>
                      <Col md="3" className="border-right">
                        <h6 className="text-muted mb-1">Sales Membership ID</h6>
                        <p className="font-weight-bold mb-0">
                          {selectedSales?._id || "-"}
                        </p>
                      </Col>
                      <Col md="3" className="border-right">
                        <h6 className="text-muted mb-1">Project</h6>
                        <p className="font-weight-bold mb-0">
                          {selectedSales?.project_id?.name || "-"}
                        </p>
                      </Col>
                      <Col md="3" className="border-right">
                        <h6 className="text-muted mb-1">Status</h6>
                        <p className="mb-0">
                          {getStatusBadge(
                            selectedSales?.subscription_id?.status
                          )}
                        </p>
                      </Col>
                      <Col md="3">
                        <h6 className="text-muted mb-1">Payment Type</h6>
                        <p className="mb-0">
                          <Badge
                            color={
                              selectedSales?.payment_type === "onetime"
                                ? "info"
                                : "primary"
                            }
                          >
                            { selectedSales?.payment_type === "onetime"
                              ? "One time"
                              : "Recurring"}
                          </Badge>
                        </p>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              {/* Sales Details */}
              <Col md="6">
                <Card className="mb-4 h-100">
                  <CardHeader>
                    <h5 className="mb-0">Sales Membership Details</h5>
                  </CardHeader>
                  <CardBody className="p-0">
                    <div className="border rounded">
                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">
                            Sales Membership ID
                          </div>
                        </Col>
                        <Col className=" d-flex justify-content-between align-items-center pe-2 ps-2">
                          <div className="fw-semibold mb-0">
                            {selectedSales?._id || "-"}
                          </div>
                          <Button
                            color="link"
                            className="p-0 ms-2 text-decoration-none"
                            onClick={handleCopySales}
                          >
                            {copiedSales ? (
                              <FontAwesomeIcon icon={faCheck} />
                            ) : (
                              <FontAwesomeIcon icon={faCopy} />
                            )}
                          </Button>
                        </Col>
                      </Row>

                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">
                            PG Membership ID
                          </div>
                        </Col>
                        <Col className=" d-flex justify-content-between align-items-center pe-2 ps-2">
                          <div className="fw-semibold mb-0 ">
                            {" "}
                            {selectedSales?.pg_subscription_id || "-"}
                          </div>
                          <Button
                            color="link"
                            className="p-0 ms-2 text-decoration-none"
                            onClick={handleCopyMem}
                          >
                            {copiedMem ? (
                              <FontAwesomeIcon icon={faCheck} />
                            ) : (
                              <FontAwesomeIcon icon={faCopy} />
                            )}
                          </Button>
                        </Col>
                      </Row>

                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">
                            Project Name
                          </div>
                        </Col>
                        <Col className="p-2">
                          <div className="fw-semibold">
                            {selectedSales?.project_id?.name || "-"}
                          </div>
                        </Col>
                      </Row>
                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">
                            Payment Gateway
                          </div>
                        </Col>
                        <Col className="p-2">
                          <div className="fw-semibold">
                            {selectedSales?.payment_gateway || "-"}
                          </div>
                        </Col>
                      </Row>
                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">
                            Payment Type
                          </div>
                        </Col>
                        <Col className="p-2">
                          <Badge
                            color={
                             selectedSales?.payment_type === "onetime"
                                ? "info"
                                : "primary"
                            }
                          >
                            { selectedSales?.payment_type === "onetime"
                              ? "One time"
                              : "Recurring"}
                          </Badge>
                        </Col>
                      </Row>
                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">Currency</div>
                        </Col>
                        <Col className="p-2">
                          {selectedSales?.currency || "-"}
                        </Col>
                      </Row>
                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">
                            Conversion Rate
                          </div>
                        </Col>
                        <Col className="p-2">
                          <div className="fw-semibold">
                            {selectedSales?.currency_conversion_rate || "-"}
                          </div>
                        </Col>
                      </Row>
                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">
                            Created Date
                          </div>
                        </Col>
                        <Col className="p-2">
                          <div className="fw-semibold">
                            <Moment format="DD MMM YYYY, hh:mm A">
                              {selectedSales?.createdAt}
                            </Moment>
                          </div>
                        </Col>
                      </Row>

                      {["plan_inr_epaper_combo_yearly", "plan_usd_epaper_combo_yearly","plan_inr_epaper_combo_30_months", "plan_usd_epaper_combo_30_months"].includes(selectedSales?.plan_id) && (
                          <Row className="g-0 border-top">
                            <Col xs="5" className="p-2 bg-light" >
                              <div className="fw-semibold text-dark">Reprocess Epaper Combo Plan</div>
                            </Col>
                            <Col className="p-1 d-flex align-items-center justify-content-start gap-4">
                              {/* Done icon if processed */}
                              {selectedSales?.is_epaper_processed ? (
                                <FontAwesomeIcon
                                  icon={faCheckCircle}
                                  className="text-success"
                                  style={{ fontSize: "1.5rem" }}
                                  title="Combo Plan Processed"
                                />
                              ) : (
                                <FontAwesomeIcon
                                  icon={faTimesCircle}
                                  color="red"
                                  style={{ fontSize: "1.5rem" }}
                                  title="Combo Plan Not Processed"
                                />
                              )}

                              {/* Reprocess button */}
                              <Button
                                color="info"
                                size="sm"
                                className="btn-round px-2 py-2"
                                onClick={() => { confirmReprocessEpaper2(selectedSales) }}
                                title="Reprocess Epaper Combo Plan"
                              >
                                <FontAwesomeIcon icon={faSyncAlt} /> <span className="ms-2">Reprocess</span>
                              </Button>
                            </Col>
                          </Row>
                        )}
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* Membership Details */}
              <Col md="6">
                <Card className="mb-4 h-100">
                  <CardHeader>
                    <h5 className="mb-0">Membership Details</h5>
                  </CardHeader>
                  <CardBody className="p-0">
                    <div className="border rounded">
                      <Row className="g-0">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">
                            Membership ID
                          </div>
                        </Col>
                        <Col className="p-2">
                          <div className="fw-semibold">
                            {selectedSales?.subscription_id
                              ?.pg_subscription_id || "-"}
                          </div>
                        </Col>
                      </Row>
                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">
                            Paid Count
                          </div>
                        </Col>
                        <Col className="p-2">
                          <Badge color="success">
                            {selectedSales?.subscription_id?.paid_count || "0"}
                          </Badge>
                        </Col>
                      </Row>
                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">Status</div>
                        </Col>
                        <Col className="p-2">
                          {getStatusBadge(
                            selectedSales?.subscription_id?.status
                          )}
                        </Col>
                      </Row>
                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">Type</div>
                        </Col>
                        <Col className="p-2">
                        <Badge
                            color={
                               selectedSales?.subscription_id?.type === "onetime"
                                ? "info"
                                : "primary"
                            }
                          >
                            { selectedSales?.subscription_id?.type === "onetime"
                              ? "One time"
                              : "Recurring"}
                          </Badge>
                        </Col>
                      </Row>
                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">Share Url</div>
                        </Col>
                        <Col className="p-2">
                          {selectedSales?.subscription_id?.share_url ? (
                            <Button
                              color="link"
                              className="p-0"
                              onClick={() =>
                                window.open(
                                  selectedSales?.subscription_id?.share_url,
                                  "_blank"
                                )
                              }
                            >
                              View URL
                            </Button>
                          ) : (
                            "-"
                          )}
                        </Col>
                      </Row>
                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">
                           Current Start Date
                          </div>
                        </Col>
                        <Col className="p-2">
                          <div className="fw-semibold">
                            {selectedSales?.subscription_id?.current_start
                              ? moment
                                  .unix(
                                    selectedSales?.subscription_id?.current_start
                                  )
                                  .format("DD MMM YYYY, hh:mm A")
                              : "-"}
                          </div>
                        </Col>
                      </Row>
                      <Row className="g-0 border-top">
                        <Col xs="5" className="p-2 bg-light">
                          <div className="fw-semibold text-dark">Current End Date</div>
                        </Col>
                        <Col className="p-2">
                          <div className="fw-semibold">
                            {selectedSales?.subscription_id?.current_end
                              ? moment
                                  .unix(selectedSales?.subscription_id?.current_end)
                                  .format("DD MMM YYYY, hh:mm A")
                              : "-"}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* Financial Information */}
              <Col md="12" className="my-4">
                <Card className="mb-4 h-100 border-0 shadow-sm" style={{ backgroundColor: "#f8f9fa" }}>
                  <CardHeader className="bg-light border-bottom">
                    <h5 className="mb-0 fw-bold text-dark">Invoice Details</h5>
                  </CardHeader>
                  <CardBody className="p-0">

                    <Row className="g-0">
                      {/* LEFT SECTION */}
                      <Col md="6" className="border-end">
                        <div className="border rounded" style={{ backgroundColor: "#f8f9fa" }}>
                          {[
                            {
                              label: "Platform",
                              value: selectedSales?.platform || "-",
                              rate: null,
                              description: null,
                              formula: null
                            },
                            {
                              label: "Payment Type",
                              value: selectedSales?.payment_type || "-",
                              rate: null,
                              description: null,
                              formula: null
                            },
                            {
                              label: "Plan ID",
                              value: selectedSales?.plan_id || "-",
                              rate: null,
                              description: null,
                              formula: null
                            },
                            {
                              label: "Txn ID",
                              value: selectedSales?.pg_txn_id || "-",
                              rate: null,
                              description: null,
                              formula: null
                            },
                            {
                              label: "Total Amount (Including GST)",
                              value: selectedSales?.txn_amount_inr,
                              rate: null,
                              description: "This is the final amount paid by user",
                              formula: "Base Amount + GST Amount"
                            },
                            {
                              label: "Base Amount (Excluding GST)",
                              value: selectedSales?.txn_amount_inr ? (selectedSales?.txn_amount_inr - (selectedSales?.gst_amount || 0)).toFixed(2) : "-",
                              rate: null,
                              description: "Amount before GST calculation",
                              formula: "Total Amount ÷ (1 + GST Rate)"
                            },
                            {
                              label: "GST Amount",
                              value: selectedSales?.gst_amount,
                              rate: `${(selectedSales?.gst_rate * 100 || 0).toFixed(2)}%`,
                              description: "Tax amount added to base amount",
                              formula: "Total Amount - Base Amount"
                            },
                            {
                              label: "PG Commission",
                              value: selectedSales?.pg_commission,
                              rate: `${(selectedSales?.pg_commission_rate * 100 || 0).toFixed(2)}%`,
                              description: "Payment gateway charges",
                              formula: "Total Amount × PG Rate"
                            },
                            {
                              label: "Marketing Fee",
                              value: selectedSales?.marketing_fee,
                              rate: `${(selectedSales?.marketing_fee_rate * 100 || 0).toFixed(2)}%`,
                              description: "Marketing and promotional charges",
                              formula: "Total Amount × Marketing Rate"
                            },
                          ].map(({ label, value, rate, description, formula }, index) => (
                            <Row
                              key={label}
                              className={`g-0 ${index !== 0 ? "border-top" : ""}`}
                            >
                              <Col xs="5" className="p-2" style={{ backgroundColor: "#e9ecef" }}>
                                <div className="fw-semibold text-dark">{label}</div>
                                {rate && (
                                  <div className="text-muted" style={{ fontSize: "0.75rem", fontWeight: "normal" }}>
                                    Rate: {rate}
                                  </div>
                                )}
                                {description && (
                                  <div className="text-muted" style={{ fontSize: "0.7rem", fontWeight: "normal", fontStyle: "italic" }}>
                                    {description}
                                  </div>
                                )}
                                {formula && (
                                  <div className="text-info" style={{ fontSize: "0.7rem", fontWeight: "normal", fontStyle: "italic" }}>
                                    {formula}
                                  </div>
                                )}
                              </Col>
                              <Col className="p-2">
                                <div className="fw-semibold">
                                  {typeof value === 'number' || (!isNaN(value) && value !== "-") ? `₹${value}` : value}
                                </div>
                              </Col>
                            </Row>
                          ))}
                        </div>
                      </Col>

                      {/* RIGHT SECTION */}
                      <Col md="6">
                        <div className="border rounded" style={{ backgroundColor: "#f8f9fa" }}>
                          {[
                            {
                              label: "Gross Amount",
                              value: selectedSales?.gross_amount,
                              rate: null,
                              description: "Total amount after deductions",
                              formula: "Base Amount - PG Commission - Marketing Fee"
                            },
                            {
                              label: "Platform Fee",
                              value: selectedSales?.platform_fee,
                              rate: `${(selectedSales?.platform_fee_rate * 100 || 0).toFixed(2)}%`,
                              description: "Platform service charges",
                              formula: "Gross Amount × Platform Rate"
                            },
                            {
                              label: "Final Net Amount",
                              value: selectedSales?.net_amount,
                              rate: null,
                              description: "Final amount available for distribution",
                              formula: "Gross Amount - Platform Fee"
                            },
                            {
                              label: "Publisher Share",
                              value: selectedSales?.publisher_share_amount,
                              rate: `${(selectedSales?.publisher_share_rate * 100 || 0).toFixed(2)}%`,
                              description: "",
                              formula: "Net Amount × Publisher Rate"
                            },
                            {
                              label: "Mediology Share",
                              value: selectedSales?.mediology_share_amount,
                              rate: `${(selectedSales?.mediology_share_rate * 100 || 0).toFixed(2)}%`,
                              description: "",
                              formula: "Net Amount × Mediology Rate"
                            },
                          ].map(({ label, value, rate, description, formula }, index) => (
                            <Row
                              key={label}
                              className={`g-0 ${index !== 0 ? "border-top" : ""}`}
                            >
                              <Col xs="5" className="p-2" style={{ backgroundColor: "#e9ecef" }}>
                                <div className="fw-semibold text-dark">{label}</div>
                                {rate && (
                                  <div className="text-muted" style={{ fontSize: "0.75rem", fontWeight: "normal" }}>
                                    Rate: {rate}
                                  </div>
                                )}
                                {description && (
                                  <div className="text-muted" style={{ fontSize: "0.7rem", fontWeight: "normal", fontStyle: "italic" }}>
                                    {description}
                                  </div>
                                )}
                                {formula && (
                                  <div className="text-info" style={{ fontSize: "0.7rem", fontWeight: "normal", fontStyle: "italic" }}>
                                    {formula}
                                  </div>
                                )}
                              </Col>
                              <Col className="p-2">
                                <div className="fw-semibold">
                                  {typeof value === 'number' || (!isNaN(value) && value !== "-") ? `₹${value}` : value}
                                </div>
                              </Col>
                            </Row>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>


            </Row>
          </CardBody>
        </Card>
      </Container>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default DetailedView;
